import { ref, watchEffect } from "vue";
import { PaymentCommonService } from "@smeetz/payment-common";
import { Channel, Payment } from "@smeetz/order-common";
import POSConfigModule from "@/store/modules/pos-config";
import DeviceModule from "@/store/modules/device";
import CashFloatModule from "@/store/modules/cash-float";
import { LogLevel, LogService } from "@/services/logging";
import {
  storePaymentToQueue,
  retrievePaymentsFromQueue,
  deletePaymentFromQueue,
} from "@/db/payments";

type Environment = "dev" | "test" | "prod";

let paymentServiceInstance: PaymentCommonService | null = null;

/**
 * Names of the prerequisites still missing on the last watchEffect run.
 * Feeds the getPaymentService error message so a missing prerequisite is
 * visible instead of a generic "not initialized".
 */
let missingPrerequisites: string[] = [];

/** Message of the last failed construction attempt, if any. */
let initFailureReason: string | null = null;

/**
 * Reactive flag: `true` while a terminal payment is being processed
 * (`createPayment` in flight). Transient and global (a single terminal handles
 * one payment at a time); intentionally not persisted.
 */
export const isPaymentInProgress = ref(false);

/**
 * Initializes the singleton instance of the PaymentCommonService.
 * This should be called once at application startup after Vuex is initialized.
 */
export function initializePaymentService(): void {
  watchEffect(() => {
    const groupId = POSConfigModule.groupInfo?.id;
    const terminalId = POSConfigModule.getPosTerminal;
    const deviceUid = DeviceModule.deviceUid;
    const salesSessionId = CashFloatModule.cashFloat?.uid;
    const servicesApi = import.meta.env.VITE_SERVICES_API as string;

    missingPrerequisites = Object.entries({
      groupId,
      salesSessionId,
      servicesApi,
    })
      .filter(([, value]) => !value)
      .map(([name]) => name);

    if (groupId && salesSessionId && servicesApi) {
      // The watcher's first run happens synchronously inside appSetup — a
      // throw here would abort the whole bootstrap, so record the failure
      // and let getPaymentService report it instead.
      try {
        paymentServiceInstance = new PaymentCommonService({
          env: import.meta.env.VITE_ENV as Environment,
          groupId,
          terminalId,
          deviceUid,
          salesSessionId,
          servicesApi,
          logServerUrl: import.meta.env.VITE_LOG_API as string,
          channel: Channel.SMEETZ_POS,
          storePayment: (payment: Payment, method: "POST" | "PATCH") =>
            storePaymentToQueue(payment, method),
          retrievePayments: (method: "POST" | "PATCH") =>
            retrievePaymentsFromQueue(method),
          deletePayment: (id: string, method: "POST" | "PATCH") =>
            deletePaymentFromQueue(id, method),
        });
        initFailureReason = null;
      } catch (error) {
        initFailureReason =
          error instanceof Error ? error.message : String(error);
        LogService.info(
          "PaymentService initialization failed",
          { error },
          LogLevel.Error
        );
      }
    }
  });
}

/**
 * Whether the payment service has been constructed. `false` while its
 * prerequisites are missing — typically before a cash float is opened
 * (no `salesSessionId`).
 */
export function isPaymentServiceReady(): boolean {
  return paymentServiceInstance !== null;
}

/**
 * Returns the singleton instance of the PaymentCommonService.
 * Throws — naming the missing prerequisites or the construction failure — if
 * the service is not ready.
 */
export function getPaymentService(): PaymentCommonService {
  if (!paymentServiceInstance) {
    throw new Error(
      `PaymentService has not been initialized. Call initializePaymentService() first. ${
        initFailureReason
          ? `Last initialization error: ${initFailureReason}`
          : `Missing prerequisites: ${
              missingPrerequisites.join(", ") || "none recorded"
            }`
      }`
    );
  }
  return paymentServiceInstance;
}
